import { useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { type GridRowId } from '@mui/x-data-grid-premium';
import { Trash2Icon } from 'lucide-react';

import type { GetUsersListQuery } from '@graphql/__generated__/graphql';
import { useLocale } from '@shared/hooks/locale';

type User = GetUsersListQuery['users'][number];

export const UsersBulkActions = ({
  users,
  selectedIds,
  loading,
  canDelete,
  deleting,
  onReassign,
  onDelete,
}: {
  users: User[];
  selectedIds: GridRowId[] | null;
  loading: boolean;
  canDelete: boolean;
  deleting: boolean;
  onReassign: (users: User[]) => void;
  onDelete: (ids: string[]) => Promise<{ failedCount: number }>;
}) => {
  const { t } = useLocale();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [failedDeleteCount, setFailedDeleteCount] = useState(0);

  return (
    <>
      <Button
        variant="action"
        disableElevation
        disabled={
          loading ||
          users.length === 0 ||
          users.length > 100 ||
          users.length !== selectedIds?.length
        }
        onClick={() => onReassign(users)}
      >
        {t('Reassign account manager')}
      </Button>
      {canDelete && (
        <>
          <LoadingButton
            disableElevation
            onClick={() => {
              setFailedDeleteCount(0);
              setDeleteOpen(true);
            }}
            loading={deleting}
            loadingPosition="start"
            startIcon={<Trash2Icon />}
            color="error"
            disabled={selectedIds == null || selectedIds.length === 0}
          >
            {t('Delete')}
          </LoadingButton>
          <Dialog
            open={deleteOpen}
            onClose={() => {
              if (!deleting) {
                setDeleteOpen(false);
              }
            }}
          >
            <DialogTitle>
              {t('Are you sure you want to delete these users?')}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                {t(
                  'Their personal data will be removed and their profiles marked as deleted. Module history will be kept.',
                )}
              </DialogContentText>
              {failedDeleteCount > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {t(
                    'Some users could not be deleted. They are still selected. Try again.',
                  )}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button disabled={deleting} onClick={() => setDeleteOpen(false)}>
                {t('Cancel')}
              </Button>
              <LoadingButton
                onClick={() => {
                  if (selectedIds == null) {
                    return;
                  }
                  void onDelete(selectedIds as string[]).then(
                    ({ failedCount }) => {
                      setFailedDeleteCount(failedCount);
                      if (failedCount === 0) {
                        setDeleteOpen(false);
                      }
                    },
                  );
                }}
                loading={deleting}
                disabled={selectedIds == null || selectedIds.length === 0}
                color="error"
                startIcon={<Trash2Icon />}
              >
                {t('Delete')}
              </LoadingButton>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};
